import { useRef, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Download,
  FileText,
  Loader2,
  Printer,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToastAction } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { formatDateBR, formatDateTime } from "@/utils/dateUtils";
import { formatCentsBRL, formatPercent } from "@/utils/money";
import { getPropostaStatusInfo, propostaFoiAceita } from "@/utils/proposta-status";
import { useToast } from "@/hooks/use-toast";
import { DocumentoEditavel } from "@/components/finance/documento/DocumentoEditavel";
import {
  useCatalogoDoModelo,
  useDocumentoEmEdicao,
  useEditarTrechoDoDocumento,
} from "@/hooks/use-finance-propostas";
import {
  AcoesDoCicloDoInvite,
  HistoricoDoCiclo,
  InviteStatusBadge,
} from "@/components/finance/proposta/CicloDaProposta";
import type {
  AcaoDoCiclo,
  DocumentoEditavel as DocumentoAberto,
  EdicaoDeTrecho,
  FormatoDocumento,
  PropostaDetail,
  PropostaDocumentoVersao,
  PropostaGrupo,
  PropostaInvite,
  PropostaPendencia,
  VarianteDoModelo,
} from "@/types/finance";

const MODELO_AUTOMATICO = "automatico";
const PARAMETRO_DA_ABA = "documento";
const PARAMETRO_DO_PROFISSIONAL = "profissional";
const ABAS_DO_DOCUMENTO = ["proposta", "invites", "historico"] as const;
type AbaDoDocumento = (typeof ABAS_DO_DOCUMENTO)[number];

type VersaoDoDocumento = Pick<
  PropostaDocumentoVersao,
  "versao" | "gerado_em" | "gerado_por_nome" | "tem_pdf" | "tem_html"
>;

function abaValida(valor: string | null): AbaDoDocumento {
  return ABAS_DO_DOCUMENTO.find((aba) => aba === valor) ?? "proposta";
}

function maisRecente<T extends { versao: number }>(versoes: T[]): T | undefined {
  return versoes.reduce<T | undefined>((maior, versao) => (!maior || versao.versao > maior.versao ? versao : maior), undefined);
}

function geradaAntesDe(versao: VersaoDoDocumento | undefined, dadosAlteradosEm: Date | null): boolean {
  return !!versao && !!dadosAlteradosEm && dadosAlteradosEm > new Date(versao.gerado_em);
}

interface EtapaDocumentoProps {
  proposta: PropostaDetail;
  editavel: boolean;
  /** Por que a proposta não aceita edição; nulo quando aceita. */
  motivoDoBloqueio: string | null;
  /** Atalho para reabrir, quando a proposta bloqueada ainda pode voltar a gerada. */
  onReabrir?: () => void;
  enfileirar: <T>(tarefa: () => Promise<T>) => Promise<T>;
  onTrocarModelo: (modelo: VarianteDoModelo | null) => void;
  onIrParaDados: () => void;
  onImprimir: (documento: DocumentoAberto) => void;
  onGerar: () => void;
  gerando: boolean;
  onGerarInvite: (freelancerId: string, nome: string) => void;
  gerandoInviteDe: string | null;
  bloqueiosDeGeracao: number;
  dadosAlteradosEm: Date | null;
  onResolverPendencia: (pendencia: PropostaPendencia) => void;
  onBaixar: (versao: number, formato: FormatoDocumento) => void;
  onBaixarInvite: (inviteId: string, versao: number, formato: FormatoDocumento) => void;
  onPedirPassoDoInvite: (inviteId: string, nome: string, acao: AcaoDoCiclo) => void;
}

export function EtapaDocumento({
  proposta,
  editavel,
  motivoDoBloqueio,
  onReabrir,
  enfileirar,
  onTrocarModelo,
  onIrParaDados,
  onImprimir,
  onGerar,
  gerando,
  onGerarInvite,
  gerandoInviteDe,
  bloqueiosDeGeracao,
  dadosAlteradosEm,
  onResolverPendencia,
  onBaixar,
  onBaixarInvite,
  onPedirPassoDoInvite,
}: EtapaDocumentoProps) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const folhaDoInviteRef = useRef<HTMLDivElement>(null);
  const aba = abaValida(searchParams.get(PARAMETRO_DA_ABA));
  const indiceSelecionado = Math.max(
    0,
    proposta.grupos.findIndex((grupo) => grupo.freelancer.id === searchParams.get(PARAMETRO_DO_PROFISSIONAL)),
  );
  const grupoSelecionado: PropostaGrupo | undefined = proposta.grupos[indiceSelecionado];
  const documentoAberto: DocumentoAberto =
    aba === "invites" && grupoSelecionado
      ? { tipo: "invite", freelancerId: grupoSelecionado.freelancer.id }
      : { tipo: "proposta" };

  const modelo = proposta.modelo_documento;
  const vagaFixa = proposta.modalidade === "fixa";
  const documentoQuery = useDocumentoEmEdicao(proposta.id, aba === "historico" ? null : documentoAberto);
  const catalogoQuery = useCatalogoDoModelo(documentoAberto.tipo, modelo.em_uso);
  const editarTrecho = useEditarTrechoDoDocumento(proposta.id);
  const chaveDoDocumento =
    documentoAberto.tipo === "invite" ? `invite-${documentoAberto.freelancerId}` : "proposta";

  const trocarParametro = (nome: string, valor: string) =>
    setSearchParams(
      (atual) => {
        const parametros = new URLSearchParams(atual);
        parametros.set(nome, valor);
        return parametros;
      },
      { replace: true },
    );

  const selecionarProfissional = (freelancerId: string) => {
    trocarParametro(PARAMETRO_DO_PROFISSIONAL, freelancerId);
    requestAnimationFrame(() =>
      folhaDoInviteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  const gravarTrecho = (secaoId: string, edicao: EdicaoDeTrecho | null) =>
    enfileirar(() => editarTrecho.mutateAsync({ documento: documentoAberto, secaoId, edicao }));

  const avisarParteAutomatica = () =>
    toast({
      title: "Esta parte vem dos dados da proposta",
      description: "Nomes, valores e datas se ajustam na etapa Dados e o documento acompanha.",
      action: (
        <ToastAction altText="Ir para Dados" onClick={onIrParaDados}>
          Ir para Dados
        </ToastAction>
      ),
    });

  const propostaFechada = proposta.status === "recusada" || proposta.status === "cancelada";
  const invitesAndandoAntesDoAceite =
    !propostaFoiAceita(proposta.status) &&
    proposta.invites.some((invite) => invite.status === "enviado" || invite.status === "aceito");
  const invitesPorFreelancer = new Map(proposta.invites.map((invite) => [invite.freelancer_id, invite]));
  const versoes = [...proposta.documentos].sort((a, b) => b.versao - a.versao);
  const ultimaVersao = versoes[0];
  const propostaDesatualizada =
    (!!ultimaVersao && proposta.cadastro_mudou) || geradaAntesDe(ultimaVersao, dadosAlteradosEm);
  const inviteDesatualizado = (invite: PropostaInvite | undefined) =>
    !!invite &&
    (invite.cadastro_mudou ||
      geradaAntesDe(maisRecente(invite.documentos), invite.dados_alterados_em ? new Date(invite.dados_alterados_em) : null));
  const algumInviteDesatualizado = proposta.invites.some(inviteDesatualizado);

  const freelancerDoItem = new Map(
    proposta.grupos.flatMap((grupo) => grupo.itens.map((item) => [item.id, grupo.freelancer.id] as const)),
  );
  const pendenciasDaProposta = proposta.pendencias.filter(
    (pendencia) => !pendencia.item_id || !freelancerDoItem.has(pendencia.item_id),
  );
  const pendenciasDoProfissional = (freelancerId: string) =>
    proposta.pendencias.filter(
      (pendencia) => !!pendencia.item_id && freelancerDoItem.get(pendencia.item_id) === freelancerId,
    );

  const inviteSelecionado = grupoSelecionado ? invitesPorFreelancer.get(grupoSelecionado.freelancer.id) : undefined;
  const ultimaVersaoDoInvite = inviteSelecionado ? maisRecente(inviteSelecionado.documentos) : undefined;
  const inviteBloqueado =
    !!grupoSelecionado &&
    [...pendenciasDaProposta, ...pendenciasDoProfissional(grupoSelecionado.freelancer.id)].some(
      (pendencia) => pendencia.bloqueia_geracao,
    );
  const grupoAnterior = proposta.grupos[indiceSelecionado - 1];
  const grupoSeguinte = proposta.grupos[indiceSelecionado + 1];
  const statusInfo = getPropostaStatusInfo(proposta.status);

  const folha = (
    <DocumentoEditavel
      key={`${chaveDoDocumento}-${modelo.em_uso}`}
      titulo={documentoAberto.tipo === "proposta" ? "Proposta" : "Invite"}
      html={documentoQuery.data?.html}
      carregando={documentoQuery.isLoading}
      comErro={documentoQuery.isError}
      onRecarregar={() => documentoQuery.refetch()}
      editavel={editavel}
      variaveis={catalogoQuery.data?.variaveis}
      onSalvarTrecho={(secaoId, edicao) => gravarTrecho(secaoId, edicao)}
      onRestaurarTrecho={(secaoId) => gravarTrecho(secaoId, null)}
      onClicarParteAutomatica={avisarParteAutomatica}
      cabecalho={<SeletorDeModelo modelo={modelo} vagaFixa={vagaFixa} disabled={!editavel} onTrocar={onTrocarModelo} />}
    />
  );

  const acoesDaFolha = (
    <div className="flex flex-wrap items-center gap-1">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="gap-1.5"
        disabled={documentoQuery.isFetching}
        onClick={() => documentoQuery.refetch()}
      >
        <RefreshCw className={documentoQuery.isFetching ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
        Atualizar
      </Button>
      <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => onImprimir(documentoAberto)}>
        <Printer className="h-3.5 w-3.5" />
        Imprimir prévia
      </Button>
    </div>
  );

  return (
    <div className="min-w-0 space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Proposta e invites</CardTitle>
          <p className="text-sm text-muted-foreground">
            Uma geração cria a proposta para a agência e um invite para cada profissional, em PDF. Cada geração fica
            guardada como uma versão no Histórico.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Button
              type="button"
              className="gap-1.5"
              disabled={!editavel || gerando || bloqueiosDeGeracao > 0}
              onClick={onGerar}
            >
              {gerando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {versoes.length === 0 ? "Gerar proposta e invites" : "Gerar nova versão"}
            </Button>
            {ultimaVersao && <DescricaoDaVersao versao={ultimaVersao} />}
            {(propostaDesatualizada || algumInviteDesatualizado) && <MarcaDesatualizado />}
          </div>
          {motivoDoBloqueio && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>{motivoDoBloqueio}</span>
              {onReabrir && (
                <Button type="button" size="sm" variant="outline" className="h-7 gap-1.5 px-2" onClick={onReabrir}>
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reabrir
                </Button>
              )}
            </div>
          )}
          {editavel && bloqueiosDeGeracao > 0 && (
            <p className="text-xs text-amber-700">
              Resolva {bloqueiosDeGeracao === 1 ? "a pendência" : `as ${bloqueiosDeGeracao} pendências`} marcadas no
              resumo para liberar a geração.
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs value={aba} onValueChange={(valor) => trocarParametro(PARAMETRO_DA_ABA, valor)} className="min-w-0">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="proposta" className="gap-1.5">
            Proposta
            {propostaDesatualizada && <PontoDeAviso rotulo="Proposta desatualizada" />}
          </TabsTrigger>
          <TabsTrigger value="invites" className="gap-1.5">
            Invites
            <span className="text-xs tabular-nums opacity-70">{proposta.grupos.length}</span>
            {algumInviteDesatualizado && <PontoDeAviso rotulo="Há invites desatualizados" />}
          </TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="proposta" className="mt-4 space-y-6">
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex flex-wrap items-center gap-2">
                <p className="min-w-0 truncate font-medium">{proposta.agencia.nome}</p>
                <Badge variant="outline" className={statusInfo.badgeClass}>
                  {statusInfo.label}
                </Badge>
                {propostaDesatualizada && <MarcaDesatualizado />}
              </div>
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Informacao rotulo="Tipo de vaga">{vagaFixa ? "Vaga fixa" : "Frila"}</Informacao>
                <Informacao rotulo="Competência">{proposta.competencia ?? "Sem competência"}</Informacao>
                <Informacao rotulo="Total da proposta">{formatCentsBRL(proposta.totais.total_cents)}</Informacao>
                <Informacao rotulo="Versão">
                  {ultimaVersao ? <DescricaoDaVersao versao={ultimaVersao} /> : "Ainda não gerada"}
                </Informacao>
              </dl>
              <ListaDePendencias pendencias={pendenciasDaProposta} onResolver={onResolverPendencia} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-3 pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Proposta para a agência</CardTitle>
                {acoesDaFolha}
              </div>
              {ultimaVersao && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>Baixar a v{ultimaVersao.versao}:</span>
                  <BotoesDeDownload
                    temPdf={ultimaVersao.tem_pdf}
                    temHtml={ultimaVersao.tem_html}
                    destaque
                    onBaixar={(formato) => onBaixar(ultimaVersao.versao, formato)}
                  />
                  {propostaDesatualizada && (
                    <AvisoDeVersaoDesatualizada
                      versao={ultimaVersao.versao}
                      gerando={gerando}
                      podeGerar={editavel && bloqueiosDeGeracao === 0}
                      onGerar={onGerar}
                    />
                  )}
                </div>
              )}
              {editavel && (
                <p className="text-xs text-muted-foreground">
                  Clique em qualquer texto para editar. Fica salvo só nesta proposta. Nomes, valores e datas vêm da
                  etapa Dados.
                </p>
              )}
            </CardHeader>
            <CardContent className="pt-0">{folha}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites" className="mt-4 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Profissionais</CardTitle>
              <p className="text-sm text-muted-foreground">Escolha o profissional para ver e editar o invite dele.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {proposta.grupos.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum profissional na proposta ainda.</p>
              ) : (
                <ul className="divide-y rounded-md border" aria-label="Profissional do invite">
                  {proposta.grupos.map((grupo) => {
                    const invite = invitesPorFreelancer.get(grupo.freelancer.id);
                    const selecionado = grupo.freelancer.id === grupoSelecionado?.freelancer.id;
                    const bloqueios = pendenciasDoProfissional(grupo.freelancer.id).filter(
                      (pendencia) => pendencia.bloqueia_geracao,
                    ).length;
                    return (
                      <li key={grupo.freelancer.id}>
                        <button
                          type="button"
                          aria-current={selecionado ? "true" : undefined}
                          onClick={() => selecionarProfissional(grupo.freelancer.id)}
                          className={cn(
                            "flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40",
                            selecionado && "bg-muted/60",
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            <span className={cn("font-medium", selecionado && "text-primary")}>
                              {grupo.freelancer.nome}
                            </span>
                            {invite?.codigo && (
                              <span className="ml-1.5 text-xs text-muted-foreground">{invite.codigo}</span>
                            )}
                          </span>
                          {bloqueios > 0 && (
                            <span className="flex items-center gap-1 text-xs text-amber-700">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              {bloqueios === 1 ? "1 pendência" : `${bloqueios} pendências`}
                            </span>
                          )}
                          {inviteDesatualizado(invite) && <MarcaDesatualizado curta />}
                          {invite ? <InviteStatusBadge status={invite.status} /> : <SemInvite />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {invitesAndandoAntesDoAceite && (
                <p className="text-xs text-amber-700">A proposta ainda não foi aceita pela agência.</p>
              )}
            </CardContent>
          </Card>

          {grupoSelecionado && (
            <>
              <Card ref={folhaDoInviteRef} className="scroll-mt-4">
                <CardContent className="space-y-3 pt-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="min-w-0 flex-1 truncate font-medium">
                      {grupoSelecionado.freelancer.nome}
                      {grupoSelecionado.cargo && (
                        <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                          {grupoSelecionado.cargo}
                        </span>
                      )}
                    </p>
                    {inviteDesatualizado(inviteSelecionado) && <MarcaDesatualizado />}
                    {inviteSelecionado ? <InviteStatusBadge status={inviteSelecionado.status} /> : <SemInvite />}
                    {inviteSelecionado && !propostaFechada && (
                      <AcoesDoCicloDoInvite
                        status={inviteSelecionado.status}
                        nome={grupoSelecionado.freelancer.nome}
                        onPedir={(acao) =>
                          onPedirPassoDoInvite(inviteSelecionado.id, grupoSelecionado.freelancer.nome, acao)
                        }
                      />
                    )}
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2"
                        disabled={!grupoAnterior}
                        onClick={() => grupoAnterior && selecionarProfissional(grupoAnterior.freelancer.id)}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        Anterior
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2"
                        disabled={!grupoSeguinte}
                        onClick={() => grupoSeguinte && selecionarProfissional(grupoSeguinte.freelancer.id)}
                      >
                        Próximo
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <ResumoDoProfissional grupo={grupoSelecionado} />
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {inviteSelecionado && ultimaVersaoDoInvite && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 px-2"
                        disabled={
                          !editavel ||
                          gerando ||
                          !!gerandoInviteDe ||
                          inviteSelecionado.status === "aceito" ||
                          inviteBloqueado
                        }
                        title={motivoDoBloqueio ?? undefined}
                        onClick={() => onGerarInvite(grupoSelecionado.freelancer.id, grupoSelecionado.freelancer.nome)}
                      >
                        {gerandoInviteDe === grupoSelecionado.freelancer.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FileText className="h-3.5 w-3.5" />
                        )}
                        Gerar nova versão deste invite
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {ultimaVersaoDoInvite ? (
                        <DescricaoDaVersao versao={ultimaVersaoDoInvite} />
                      ) : (
                        "O invite deste profissional ainda não foi gerado."
                      )}
                    </p>
                  </div>
                  {inviteSelecionado?.status === "aceito" && editavel && (
                    <p className="text-xs text-muted-foreground">
                      O profissional já aceitou este invite. Para gerar uma nova versão, reabra o invite antes.
                    </p>
                  )}
                  <ListaDePendencias
                    pendencias={pendenciasDoProfissional(grupoSelecionado.freelancer.id)}
                    onResolver={onResolverPendencia}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base">Invite: {grupoSelecionado.freelancer.nome}</CardTitle>
                    {acoesDaFolha}
                  </div>
                  {inviteSelecionado && ultimaVersaoDoInvite && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>Baixar a v{ultimaVersaoDoInvite.versao}:</span>
                      <BotoesDeDownload
                        temPdf={ultimaVersaoDoInvite.tem_pdf}
                        temHtml={ultimaVersaoDoInvite.tem_html}
                        destaque
                        onBaixar={(formato) =>
                          onBaixarInvite(inviteSelecionado.id, ultimaVersaoDoInvite.versao, formato)
                        }
                      />
                      {inviteDesatualizado(inviteSelecionado) && (
                        <AvisoDeVersaoDesatualizada
                          versao={ultimaVersaoDoInvite.versao}
                          gerando={gerandoInviteDe === grupoSelecionado.freelancer.id}
                          podeGerar={
                            editavel && !gerando && !gerandoInviteDe && inviteSelecionado.status !== "aceito" && !inviteBloqueado
                          }
                          onGerar={() => onGerarInvite(grupoSelecionado.freelancer.id, grupoSelecionado.freelancer.nome)}
                        />
                      )}
                    </div>
                  )}
                  {editavel && (
                    <p className="text-xs text-muted-foreground">
                      Clique em qualquer texto para editar. Fica salvo só nesta proposta, no invite deste
                      profissional. Nomes, valores e datas vêm da etapa Dados.
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pt-0">{folha}</CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="historico" className="mt-4 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Versões geradas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {versoes.length === 0 && proposta.invites.every((invite) => invite.documentos.length === 0) ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma versão gerada ainda. Cada geração da proposta e dos invites aparece aqui.
                </p>
              ) : (
                <>
                  <ListaDeVersoes
                    titulo="Proposta para a agência"
                    versoes={versoes}
                    desatualizada={propostaDesatualizada}
                    onBaixar={onBaixar}
                  />
                  {proposta.grupos.map((grupo) => {
                    const invite = invitesPorFreelancer.get(grupo.freelancer.id);
                    if (!invite || invite.documentos.length === 0) return null;
                    return (
                      <ListaDeVersoes
                        key={invite.id}
                        titulo={`Invite: ${grupo.freelancer.nome}${invite.codigo ? ` (${invite.codigo})` : ""}`}
                        versoes={[...invite.documentos].sort((a, b) => b.versao - a.versao)}
                        desatualizada={inviteDesatualizado(invite)}
                        onBaixar={(versao, formato) => onBaixarInvite(invite.id, versao, formato)}
                      />
                    );
                  })}
                </>
              )}
            </CardContent>
          </Card>

          <HistoricoDoCiclo eventos={proposta.eventos} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ResumoDoProfissional({ grupo }: { grupo: PropostaGrupo }) {
  const valorDoProfissional = grupo.itens.reduce((soma, item) => soma + (item.valor_profissional_cents ?? 0), 0);
  const comissao = grupo.itens.reduce((soma, item) => soma + (item.comissao_cents ?? 0), 0);
  const quemCobra = [
    ...new Set(
      grupo.itens.map((item) =>
        item.modo_freelancer === "reter"
          ? `${item.empresa_agencia?.nome ?? "Corisco"} repassa o líquido`
          : `${item.empresa_freelancer?.nome ?? "Faísca"} cobra a comissão`,
      ),
    ),
  ];
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 xl:grid-cols-4">
      <Informacao rotulo={grupo.itens.length === 1 ? "Período" : "Períodos"}>
        {grupo.itens.length === 0 ? (
          "Sem períodos"
        ) : (
          <ul>
            {grupo.itens.map((item) => (
              <li key={item.id}>
                {item.data_inicio ? formatDateBR(item.data_inicio) : "Sem início"} a{" "}
                {item.data_conclusao ? formatDateBR(item.data_conclusao) : "sem fim"}
              </li>
            ))}
          </ul>
        )}
      </Informacao>
      <Informacao rotulo="Valor do profissional">{formatCentsBRL(valorDoProfissional)}</Informacao>
      <Informacao rotulo="Comissão">
        {grupo.comissao_percentual === 0
          ? "Isento"
          : `${formatPercent(grupo.comissao_percentual)}% (${formatCentsBRL(comissao)})`}
      </Informacao>
      <Informacao rotulo="Empresa">{quemCobra.length > 0 ? quemCobra.join("; ") : "Sem períodos"}</Informacao>
    </dl>
  );
}

function Informacao({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{rotulo}</dt>
      <dd className="font-medium tabular-nums">{children}</dd>
    </div>
  );
}

function DescricaoDaVersao({ versao }: { versao: VersaoDoDocumento }) {
  return (
    <span className="text-xs font-normal text-muted-foreground">
      Versão {versao.versao}, gerada em {formatDateTime(versao.gerado_em, "dd/MM")}
      {versao.gerado_por_nome ? ` por ${versao.gerado_por_nome}` : ""}
    </span>
  );
}

function MarcaDesatualizado({ curta = false }: { curta?: boolean }) {
  return (
    <Badge
      variant="outline"
      className="border-amber-200 bg-amber-50 text-[10px] text-amber-700"
      title="Os dados mudaram depois desta versão"
    >
      {curta ? "Desatualizado" : "Desatualizado: gere novamente"}
    </Badge>
  );
}

function AvisoDeVersaoDesatualizada({
  versao,
  gerando,
  podeGerar,
  onGerar,
}: {
  versao: number;
  gerando: boolean;
  podeGerar: boolean;
  onGerar: () => void;
}) {
  return (
    <>
      <span className="text-amber-700">A v{versao} é de antes da última alteração e não traz os dados da prévia.</span>
      {podeGerar && (
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1.5 px-2" disabled={gerando} onClick={onGerar}>
          {gerando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Gerar nova versão
        </Button>
      )}
    </>
  );
}

function PontoDeAviso({ rotulo }: { rotulo: string }) {
  return <span className="h-1.5 w-1.5 rounded-full bg-amber-500" role="img" aria-label={rotulo} title={rotulo} />;
}

function SemInvite() {
  return (
    <Badge variant="outline" className="text-[10px] text-muted-foreground">
      Ainda não gerado
    </Badge>
  );
}

function ListaDePendencias({
  pendencias,
  onResolver,
}: {
  pendencias: PropostaPendencia[];
  onResolver: (pendencia: PropostaPendencia) => void;
}) {
  if (pendencias.length === 0) return null;
  const ordenadas = [
    ...pendencias.filter((pendencia) => pendencia.bloqueia_geracao),
    ...pendencias.filter((pendencia) => !pendencia.bloqueia_geracao),
  ];
  return (
    <ul className="space-y-1">
      {ordenadas.map((pendencia, indice) => (
        <li key={`${pendencia.codigo}-${pendencia.item_id ?? ""}-${indice}`}>
          <button
            type="button"
            onClick={() => onResolver(pendencia)}
            className={cn(
              "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
              pendencia.bloqueia_geracao
                ? "bg-amber-50 text-amber-900 hover:bg-amber-100"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {pendencia.bloqueia_geracao ? (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
              <CircleDot className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            {pendencia.mensagem}
          </button>
        </li>
      ))}
    </ul>
  );
}

function ListaDeVersoes({
  titulo,
  versoes,
  desatualizada = false,
  onBaixar,
}: {
  titulo: string;
  versoes: VersaoDoDocumento[];
  desatualizada?: boolean;
  onBaixar: (versao: number, formato: FormatoDocumento) => void;
}) {
  if (versoes.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{titulo}</p>
      <ul className="divide-y rounded-md border">
        {versoes.map((documento, indice) => (
          <li key={documento.versao} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <span className="font-medium">v{documento.versao}</span>
            {indice === 0 && desatualizada && <MarcaDesatualizado />}
            <span className="flex-1 text-xs text-muted-foreground">
              {formatDateTime(documento.gerado_em)}
              {documento.gerado_por_nome ? ` · ${documento.gerado_por_nome}` : ""}
            </span>
            <BotoesDeDownload
              temPdf={documento.tem_pdf}
              temHtml={documento.tem_html}
              destaque={indice === 0}
              onBaixar={(formato) => onBaixar(documento.versao, formato)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function BotoesDeDownload({
  temPdf,
  temHtml,
  destaque,
  onBaixar,
}: {
  temPdf: boolean;
  temHtml: boolean;
  destaque: boolean;
  onBaixar: (formato: FormatoDocumento) => void;
}) {
  return (
    <span className="flex items-center gap-1">
      {temPdf && (
        <Button
          type="button"
          size="sm"
          variant={destaque ? "default" : "outline"}
          className="h-7 gap-1"
          onClick={() => onBaixar("pdf")}
        >
          <Download className="h-3.5 w-3.5" />
          PDF
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 gap-1"
        disabled={!temHtml}
        onClick={() => onBaixar("html")}
      >
        HTML
      </Button>
    </span>
  );
}

function SeletorDeModelo({
  modelo,
  vagaFixa,
  disabled,
  onTrocar,
}: {
  modelo: PropostaDetail["modelo_documento"];
  vagaFixa: boolean;
  disabled: boolean;
  onTrocar: (modelo: VarianteDoModelo | null) => void;
}) {
  const rotulo = (id: VarianteDoModelo) => modelo.opcoes.find((opcao) => opcao.id === id)?.rotulo ?? id;
  return (
    <Select
      value={modelo.escolhido ?? MODELO_AUTOMATICO}
      disabled={disabled}
      onValueChange={(valor) => onTrocar(valor === MODELO_AUTOMATICO ? null : (valor as VarianteDoModelo))}
    >
      <SelectTrigger className="h-7 w-auto gap-1.5 border-dashed px-2 text-xs" aria-label="Modelo do documento">
        <span className="text-muted-foreground">{vagaFixa ? "Modelo de vaga fixa:" : "Modelo:"}</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={MODELO_AUTOMATICO}>
          {rotulo(modelo.automatico)} <span className="text-muted-foreground">(de quem fatura a agência)</span>
        </SelectItem>
        {modelo.opcoes.map((opcao) => (
          <SelectItem key={opcao.id} value={opcao.id}>
            {opcao.rotulo}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
